import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { Button, FullPageStatus } from "@vv/ui";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts/PieChart";
import { useEffect } from "react";
import {
  REQUEST_STATUS_LABELS,
  VALIDATION_RESULT_LABELS,
  type RequestStatus,
  type ValidationResult,
} from "../domain/analytics.types";
import { fetchAnalytics } from "../state/analytics.thunks";
import { useAnalyticsDispatch, useAnalyticsSelector } from "../state/hooks";

interface StatCardProps {
  label: string;
  value: string;
}

function StatCard({ label, value }: StatCardProps) {
  return (
    <Card variant="outlined" sx={{ flex: 1, minWidth: 160 }}>
      <CardContent>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
}

export function AnalyticsPage() {
  const dispatch = useAnalyticsDispatch();
  const summary = useAnalyticsSelector((state) => state.analytics.summary);
  const byTeam = useAnalyticsSelector((state) => state.analytics.byTeam);
  const status = useAnalyticsSelector((state) => state.analytics.status);
  const error = useAnalyticsSelector((state) => state.analytics.error);

  useEffect(() => {
    dispatch(fetchAnalytics());
  }, [dispatch]);

  if (status === "loading" && !summary) {
    return <FullPageStatus variant="loading" title="Loading analytics..." />;
  }

  if (status === "failed" && !summary) {
    return (
      <FullPageStatus
        variant="error"
        title="Could not load analytics"
        description={error ?? undefined}
        action={
          <Button variant="contained" onClick={() => dispatch(fetchAnalytics())}>
            Retry
          </Button>
        }
      />
    );
  }

  if (!summary) {
    return null;
  }

  const statusData = Object.entries(summary.byStatus)
    .filter(([, count]) => count > 0)
    .map(([status, count]) => ({
      id: status,
      value: count,
      label: REQUEST_STATUS_LABELS[status as RequestStatus],
    }));

  const resultData = Object.entries(summary.byValidationResult)
    .filter(([, count]) => count > 0)
    .map(([result, count]) => ({
      id: result,
      value: count,
      label: VALIDATION_RESULT_LABELS[result as ValidationResult],
    }));

  const totalCompletions = summary.onTimeCompletions + summary.lateCompletions;
  const onTimeRate =
    totalCompletions > 0
      ? `${Math.round((summary.onTimeCompletions / totalCompletions) * 100)}%`
      : "—";

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
        Analytics
      </Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 4, flexWrap: "wrap" }}>
        <StatCard label="Total requests" value={String(summary.totalRequests)} />
        <StatCard label="On-time completion rate" value={onTimeRate} />
        <StatCard label="Late completions" value={String(summary.lateCompletions)} />
        <StatCard
          label="Average completion time"
          value={
            summary.averageCompletionDays === null
              ? "—"
              : `${summary.averageCompletionDays.toFixed(1)}d`
          }
        />
      </Stack>

      <Stack direction="row" spacing={3} sx={{ mb: 4, flexWrap: "wrap" }}>
        <Card variant="outlined" sx={{ flex: 1, minWidth: 320 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Requests by status
            </Typography>
            {statusData.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No requests yet.
              </Typography>
            ) : (
              <PieChart series={[{ data: statusData }]} height={240} />
            )}
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ flex: 1, minWidth: 320 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Validation results
            </Typography>
            {resultData.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No completed validations yet.
              </Typography>
            ) : (
              <PieChart series={[{ data: resultData }]} height={240} />
            )}
          </CardContent>
        </Card>
      </Stack>

      <Card variant="outlined" sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Requests by team
          </Typography>
          {byTeam.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No teams yet.
            </Typography>
          ) : (
            <BarChart
              height={280}
              xAxis={[{ scaleType: "band", data: byTeam.map((team) => team.teamName) }]}
              series={[
                { data: byTeam.map((team) => team.openRequests), label: "Open" },
                { data: byTeam.map((team) => team.closedRequests), label: "Closed" },
              ]}
            />
          )}
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Team breakdown
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Team</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Open</TableCell>
                  <TableCell align="right">Closed</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {byTeam.map((team) => (
                  <TableRow key={team.teamId}>
                    <TableCell>{team.teamName}</TableCell>
                    <TableCell align="right">{team.totalRequests}</TableCell>
                    <TableCell align="right">{team.openRequests}</TableCell>
                    <TableCell align="right">{team.closedRequests}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
}
